import {initFlatPickerInput} from "../../helpers/inputs/flatPicker/flatPicker";
import {createReplaceableElement} from "../../helpers/replaceableElement";
import createElement from "../../helpers/createElement";
import Axios from "axios";
import {
    decorateCalendarEventDateDay,
    decorateCalendarEventDates as decorateFlatpickrEventDates,
} from "../../helpers/calendar/decorateCalendarEventDates";

/**
 * Initialize the public calendar index
 */
function initPublicCalendarIndex() {
    const calendarInput = document.querySelector('.calendar-dummy-input');
    const resultsContainer = document.querySelector('.calendar-events-results-list');
    let imageLightbox = null;
    let requestGeneration = 0;
    let completionTimer = null;

    if (!calendarInput || !resultsContainer) {
        return;
    }

    const refreshRoute = resultsContainer.dataset.refreshRoute;
    const dateColorMetadata = getDateColorMetadata(calendarInput);
    let flatpickrInstance = null;

    // Initialize replaceable element for results
    const replaceableResults = createReplaceableElement({
        element: resultsContainer,
        init: function () {
            initEventImageLightboxTriggers(this.element);
        }
    });

    // Initialize flatpickr in inline mode
    flatpickrInstance = initFlatPickerInput(calendarInput, {
        inline: true,
        mode: 'range',
        onDayCreate: function (selectedDates, dateStr, instance, day) {
            decorateCalendarEventDateDay(day, instance, dateColorMetadata);
        },
        onReady: function () {
            decorateCalendarEventDates();
            scheduleCalendarEventDateDecoration();
        },
        onMonthChange: function () {
            decorateCalendarEventDates();
            scheduleCalendarEventDateDecoration();
            startCalendarEventDateDecorationFallback();
        },
        onYearChange: function () {
            decorateCalendarEventDates();
            scheduleCalendarEventDateDecoration();
            startCalendarEventDateDecorationFallback();
        },
        onChange: function (selectedDates, dateStr) {
            decorateCalendarEventDates();
            scheduleCalendarEventDateDecoration();
            startCalendarEventDateDecorationFallback();

            // Wait until a single date or a full range is selected
            if (selectedDates.length === 0) {
                return;
            }

            // Remove active class from all shortcut buttons on change, let the button add them back
            const shortcutButtons = document.querySelectorAll('.calendar-shortcuts-container button');
            shortcutButtons.forEach(b => b.classList.remove('active'));

            const data = {};
            if (selectedDates.length === 1) {
                data.date = dateStr;
            } else if (selectedDates.length === 2) {
                data.date_range = dateStr;
            }

            refreshResults(data);
        },
    });

    // Handle shortcuts
    const shortcutButtons = document.querySelectorAll('.calendar-shortcuts-container button');
    shortcutButtons.forEach(function (button) {
        button.addEventListener('click', function () {
            const range = button.dataset.range;
            const startDate = new Date();
            let endDate = new Date();

            if (range === 'week') {
                // Set to current day and end of week (Saturday)
                const day = startDate.getDay();
                endDate = new Date(startDate);
                endDate.setDate(startDate.getDate() + (6 - day));
            } else if (range === 'month') {
                endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
            } else if (range === 'next30') {
                endDate = new Date(startDate);
                endDate.setDate(startDate.getDate() + 30);
            }

            // setDate(..., true) triggers onChange, which will clear the active class
            flatpickrInstance.setDate([startDate, endDate], true);

            // Re-add active class to this button
            button.classList.add('active');
        });
    });

    // Click first shortcut button if available (should be week button)
    if (shortcutButtons.item(0)) {
        shortcutButtons.item(0).click();
    }

    decorateCalendarEventDates();
    scheduleCalendarEventDateDecoration();
    startCalendarEventDateDecorationFallback();

    // Methods

    /**
     * Read backend-built date color metadata from the stable JSON script.
     *
     * @param {HTMLInputElement} input
     * @return {Object<string, {displayColor: string, displayTextColor: string, isMixed: boolean}>}
     */
    function getDateColorMetadata(input) {
        const scriptSelector = input.dataset.calendarDateColorMetadataScript;
        const script = scriptSelector ? document.querySelector(scriptSelector) : null;

        if (!script || !script.textContent.trim()) {
            return {};
        }

        try {
            return JSON.parse(script.textContent);
        } catch (error) {
            console.error('Error parsing calendar date color metadata:', error);
            return {};
        }
    }

    /**
     * Reapply event-date styles after Flatpickr finishes its DOM updates.
     */
    function scheduleCalendarEventDateDecoration() {
        window.setTimeout(decorateCalendarEventDates, 16);
    }

    /**
     * Flatpickr can finish day-cell creation after lifecycle hooks in Dusk/browser refreshes.
     */
    function startCalendarEventDateDecorationFallback() {
        let remainingAttempts = 20;
        const intervalId = window.setInterval(function () {
            decorateCalendarEventDates();
            remainingAttempts--;

            if (remainingAttempts <= 0 || flatpickrInstance.calendarContainer.querySelector('.calendar-event-date')) {
                window.clearInterval(intervalId);
            }
        }, 50);
    }

    /**
     * Decorate Flatpickr day cells from backend-provided date color metadata.
     */
    function decorateCalendarEventDates() {
        decorateFlatpickrEventDates(flatpickrInstance, dateColorMetadata);
    }

    /**
     * Refresh the events list
     *
     * @param {Object} data
     */
    function refreshResults(data) {
        const generation = ++requestGeneration;
        window.clearTimeout(completionTimer);
        completionTimer = null;
        replaceableResults.startLoading();

        Axios.post(refreshRoute, data)
            .then(function (response) {
                if (generation !== requestGeneration) {
                    return;
                }

                const newElement = createElement(response.data.html);
                // Prevent replace()'s unowned timer; calendar completion belongs to this request.
                replaceableResults.stopLoading();
                replaceableResults.replace(newElement);
                replaceableResults.startLoading();
                completionTimer = window.setTimeout(function () {
                    if (generation === requestGeneration && replaceableResults.element === newElement) {
                        replaceableResults.stopLoading();
                        completionTimer = null;
                    }
                }, 32);
            })
            .catch(function (error) {
                if (generation !== requestGeneration) {
                    return;
                }

                window.clearTimeout(completionTimer);
                completionTimer = null;
                console.error('Error fetching events:', error);
                replaceableResults.stopLoading();
            });
    }

    /**
     * Initialize event image lightbox triggers in the current results markup.
     *
     * @param {Element} container
     */
    function initEventImageLightboxTriggers(container) {
        const images = container.querySelectorAll('.calendar-event-lightbox-image');

        images.forEach(function (image) {
            image.addEventListener('click', function () {
                displayEventImageLightbox(image);
            });

            image.addEventListener('keydown', function (event) {
                if (event.key !== 'Enter' && event.key !== ' ') {
                    return;
                }

                event.preventDefault();
                displayEventImageLightbox(image);
            });
        });
    }

    /**
     * Display the selected event image in an almost fullscreen overlay.
     *
     * @param {HTMLImageElement} sourceImage
     */
    function displayEventImageLightbox(sourceImage) {
        const imageSrc = sourceImage.dataset.lightboxSrc || sourceImage.currentSrc || sourceImage.src;

        if (!imageSrc) {
            return;
        }

        const lightbox = getEventImageLightbox();
        const previewImage = lightbox.querySelector('.calendar-image-lightbox-image');

        previewImage.src = imageSrc;
        previewImage.alt = sourceImage.getAttribute('alt') || '';

        lightbox.classList.add('is-visible');
        lightbox.setAttribute('aria-hidden', 'false');
        document.body.classList.add('calendar-image-lightbox-open');

        lightbox.querySelector('.calendar-image-lightbox-close').focus();
    }

    /**
     * Build the shared lightbox element the first time an image is opened.
     *
     * @returns {Element}
     */
    function getEventImageLightbox() {
        if (imageLightbox) {
            return imageLightbox;
        }

        imageLightbox = createElement(`
            <div class="calendar-image-lightbox" aria-hidden="true">
                <div class="calendar-image-lightbox-content" role="dialog" aria-modal="true" aria-label="Event image preview">
                    <button type="button" class="calendar-image-lightbox-close" aria-label="Close image preview">&times;</button>
                    <img class="calendar-image-lightbox-image" src="" alt="">
                </div>
            </div>
        `);

        imageLightbox.addEventListener('click', function (event) {
            const closeButton = event.target.closest ? event.target.closest('.calendar-image-lightbox-close') : null;

            if (event.target === imageLightbox || closeButton) {
                closeEventImageLightbox();
            }
        });

        document.addEventListener('keydown', function (event) {
            if (event.key === 'Escape' && imageLightbox.classList.contains('is-visible')) {
                closeEventImageLightbox();
            }
        });

        document.body.appendChild(imageLightbox);

        return imageLightbox;
    }

    /**
     * Hide the event image lightbox.
     */
    function closeEventImageLightbox() {
        if (!imageLightbox) {
            return;
        }

        imageLightbox.classList.remove('is-visible');
        imageLightbox.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('calendar-image-lightbox-open');
    }
}

export {initPublicCalendarIndex}
